// # Calendar Event Chip Component
// Compact event display for calendar cells with source type badges
// Responsive design for mobile and desktop

'use client';

import React from 'react';
import { Icon, IconMap } from '../icons';
import type { CalendarEvent } from '../../types/calendar';
import { useDeviceInfo } from '../../hooks/useDeviceInfo';
import { Send } from 'lucide-react';

interface CalendarEventChipProps {
  event: CalendarEvent;
  onClick?: (event: CalendarEvent) => void;
  compact?: boolean; // For very small spaces (mobile calendar cells)
  className?: string;
}

// # Source type configuration
const SOURCE_TYPE_CONFIG: Record<string, { 
  iconName: keyof typeof IconMap;
  label: string; 
  color: string;
  bgColor: string;
  textColor: string;
}> = {
  task: {
    iconName: 'briefcase', // Using briefcase as assignment isn't in IconMap
    label: 'Task',
    color: '#3b82f6',
    bgColor: 'bg-blue-50',
    textColor: 'text-blue-700'
  },
  reminder: {
    iconName: 'alert-circle', // Using alert-circle as notifications isn't in IconMap
    label: 'Reminder',
    color: '#8b5cf6',
    bgColor: 'bg-purple-50',
    textColor: 'text-purple-700'
  },
  lead: {
    iconName: 'person',
    label: 'Lead',
    color: '#1F4A6E',
    bgColor: 'bg-cyan-50',
    textColor: 'text-cyan-700'
  },
  sale: {
    iconName: 'dollar-sign', // Maps to 'attach-money' in Material Icons
    label: 'Sale',
    color: '#10b981',
    bgColor: 'bg-green-50',
    textColor: 'text-green-700'
  },
  payment_installment: {
    iconName: 'dollar-sign', // Using dollar-sign for payment
    label: 'Payment',
    color: '#f59e0b',
    bgColor: 'bg-amber-50',
    textColor: 'text-amber-700'
  },
  calendar: {
    iconName: 'calendar',
    label: 'Event',
    color: '#6b7280',
    bgColor: 'bg-gray-50',
    textColor: 'text-gray-700'
  }
};

export function CalendarEventChip({
  event,
  onClick,
  compact = false,
  className = ''
}: CalendarEventChipProps) {
  const { isMobile } = useDeviceInfo();
  const sourceType = event.source_type || 'calendar';
  const config = SOURCE_TYPE_CONFIG[sourceType] || SOURCE_TYPE_CONFIG.calendar;
  const eventColor = event.color || config.color;

  // # Format time for display
  const formatTime = () => {
    if (event.all_day) return null;
    if (!event.start_time) return null;
    return event.start_time.slice(0, 5); // HH:MM
  };

  const time = formatTime();
  const isPast = new Date(event.end_date) < new Date();

  // # Ultra-compact version for mobile calendar cells
  if (compact && isMobile) {
    return (
      <div
        onClick={(e) => {
          e.stopPropagation();
          onClick?.(event);
        }}
        className={`rounded truncate cursor-pointer hover:opacity-80 transition-opacity flex items-center gap-0.5 ${className}`}
        style={{
          borderLeft: `2px solid ${event.is_favorite ? '#fbbf24' : eventColor}`,
          backgroundColor: event.is_favorite ? '#fef9c3' : `${eventColor}15`
        }}
        title={event.title}
      >
        {event.is_favorite && (
          <Icon 
            name="star"
            size={8}
            className="text-yellow-500 fill-current flex-shrink-0"
            style={{ color: '#fbbf24' }}
          />
        )}
        <span className={`text-gray-900 font-medium block truncate ${
          isPast ? 'opacity-60' : ''
        }`} style={{ fontSize: '9px', padding: '1px 2px' }}>
          {event.title}
        </span>
      </div>
    );
  }

  // # Compact version for desktop calendar cells
  if (compact) {
    return (
      <div
        onClick={(e) => {
          e.stopPropagation();
          onClick?.(event);
        }}
        className={`rounded cursor-pointer hover:opacity-80 transition-opacity flex items-center gap-1 ${className}`}
        style={{
          borderLeft: `2px solid ${event.is_favorite ? '#fbbf24' : eventColor}`,
          backgroundColor: event.is_favorite ? '#fef9c3' : `${eventColor}15`
        }}
        title={event.title}
      >
        {event.is_favorite && (
          <Icon 
            name="star"
            size={8}
            className="text-yellow-500 fill-current flex-shrink-0"
            style={{ color: '#fbbf24' }}
          />
        )}
        <Icon 
          name={config.iconName}
          size={10}
          color={event.is_favorite ? '#fbbf24' : eventColor}
          style={{ flexShrink: 0 }}
        />
        <span className={`text-gray-900 font-medium truncate text-[9px] ${
          isPast ? 'opacity-60' : ''
        }`} style={{ padding: '2px 4px' }}>
          {event.title}
        </span>
        {time && (
          <span className="text-gray-500 text-[8px] whitespace-nowrap">
            {time}
          </span>
        )}
      </div>
    );
  }

  // # Enhanced standard chip version (for list views)
  return (
    <div
      onClick={() => onClick?.(event)}
      className={`group rounded-xl cursor-pointer hover:shadow-xl transition-all duration-200 flex items-center gap-3 border-2 ${
        event.is_favorite 
          ? 'border-yellow-300 bg-yellow-50' 
          : 'border-transparent'
      } hover:border-${eventColor.replace('#', '')} ${
        isMobile ? 'p-3' : 'p-4'
      } ${className} ${isPast ? 'opacity-70' : ''}`}
      style={{
        borderLeft: `4px solid ${event.is_favorite ? '#fbbf24' : eventColor}`,
        backgroundColor: event.is_favorite ? '#fef9c3' : `${eventColor}12`,
        boxShadow: event.is_favorite ? '0 2px 6px rgba(251, 191, 36, 0.3)' : '0 1px 3px rgba(0,0,0,0.06)'
      }}
    >
      {/* Source type icon with animated background */}
      <div 
        className={`${config.bgColor} rounded-xl flex items-center justify-center flex-shrink-0 shadow-md group-hover:scale-110 transition-transform duration-200`}
        style={{ 
          width: isMobile ? '44px' : '52px', 
          height: isMobile ? '44px' : '52px' 
        }}
      >
        <Icon 
          name={config.iconName}
          size={isMobile ? 20 : 24}
          color={eventColor}
        />
      </div>

      {/* Event content */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap mb-1.5">
          <h4 className={`font-bold truncate ${
            isMobile ? 'text-sm' : 'text-base'
          } ${isPast ? 'text-gray-500' : 'text-gray-900'} group-hover:text-blue-700 transition-colors`}>
            {event.title}
          </h4>
          {/* Favorite star icon */}
          {event.is_favorite && (
            <Icon 
              name="star" 
              size={isMobile ? 14 : 16} 
              className="text-yellow-500 fill-current flex-shrink-0"
              style={{ color: '#fbbf24' }}
            />
          )}
          {/* Source type badge */}
          <span className={`px-2.5 py-1 rounded-full text-[10px] font-bold whitespace-nowrap shadow-sm ${
            config.bgColor
          } ${config.textColor}`}>
            {config.label}
          </span>
          {/* Contacted badge for lead events */}
          {event.source_type === 'lead' && event.custom_fields?.has_emailed && (
            <span className="px-1.5 py-0.5 text-[10px] font-bold rounded-full bg-blue-100 text-blue-800 border border-blue-300 flex items-center gap-0.5 shadow-sm" title="Contacted (email sent)">
              <Send className="w-2.5 h-2.5" />
              Contacted
            </span>
          )}
        </div>
        
        {/* Time and additional info with icons */}
        <div className="flex items-center gap-3 mt-1">
          {time && (
            <div className="flex items-center gap-1">
              <Icon name="clock" size={isMobile ? 12 : 14} color="#6b7280" />
              <span className={`text-gray-600 font-medium ${isMobile ? 'text-xs' : 'text-sm'}`}>
                {time}
              </span>
            </div>
          )}
          {event.location && (
            <div className="flex items-center gap-1">
              <Icon name="location" size={isMobile ? 12 : 14} color="#6b7280" />
              <span className={`text-gray-600 truncate max-w-[120px] ${isMobile ? 'text-xs' : 'text-sm'}`}>
                {event.location}
              </span>
            </div>
          )}
          {event.source_data && (
            <>
              {event.source_data.priority && (
                <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold shadow-sm ${
                  event.source_data.priority === 'urgent' 
                    ? 'bg-red-100 text-red-700 animate-pulse'
                    : event.source_data.priority === 'high'
                    ? 'bg-orange-100 text-orange-700'
                    : 'bg-gray-100 text-gray-600'
                }`}>
                  {event.source_data.priority.toUpperCase()}
                </span>
              )}
              {event.source_data.status && (
                <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold bg-gray-100 text-gray-700 shadow-sm`}>
                  {event.source_data.status}
                </span>
              )}
            </>
          )}
        </div>

        {/* Description preview if available */}
        {event.description && !isMobile && (
          <p className="text-xs text-gray-600 mt-2 line-clamp-1">
            {event.description}
          </p>
        )}
      </div>

      {/* Hover indicator arrow */}
      <div className="flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
        <Icon name="chevron-right" size={20} color={eventColor} />
      </div>
    </div>
  );
}

export default CalendarEventChip;

